import React, { useEffect, useState } from 'react';
import { Button, Modal, Select, Table, message } from 'antd';
import axios from 'axios';
import AuthContext from '../../../services/Auth';
import {
  DELETE_ALLOCATION_URL,
  GET_ASSIGNED_PROJECTS_URL,
  GET_PROJECT_USERS_URL,
  masterHostKeyCode,
  subscribeKey,
} from '../../../constant';
import { formatSriLankaDate, formatSriLankaDateTime } from '../../../utils/sriLankaTime';
import { uniqueBy } from '../../../utils/uniqueOptions';
import '../../../assets/css/resourceAllocation.scss';

const normalizeResponse = (result) => {
  if (!result) {
    return [];
  }

  if (Array.isArray(result)) {
    return result;
  }

  if (Array.isArray(result.data)) {
    return result.data;
  }

  if (Array.isArray(result.projects)) {
    return result.projects;
  }

  if (Array.isArray(result.users)) {
    return result.users;
  }

  if (result.projectId || result.id || result.userId) {
    return [result];
  }

  return [];
};

const mapProjectOption = (project) => {
  const projectId = project.projectId ?? project.id;
  const projectName = project.projectName ?? project.name ?? project.title ?? `Project ${projectId}`;
  const projectCode = project.projectCode ?? project.code;

  return {
    value: projectId,
    label: projectCode ? `${projectName} (${projectCode})` : projectName,
  };
};

const mapUserRow = (row) => ({
  key: `${row.userId ?? row.UserId}-${row.projectId ?? row.ProjectId}-${row.allocId ?? row.AllocId ?? ''}`,
  userId: row.userId ?? row.UserId,
  userName: row.userName ?? row.UserName ?? row.name ?? row.Name ?? 'Unknown',
  projectId: row.projectId ?? row.ProjectId,
  projectName: row.projectName ?? row.ProjectName ?? row.name ?? row.Name ?? 'Unknown',
  startDate: row.startDate ?? row.StartDate,
  endDate: row.endDate ?? row.EndDate,
  billablePercentage: row.billablePercentage ?? row.BillablePercentage ?? row.billable ?? row.Billable ?? 0,
  nonBillablePercentage: row.nonBillablePercentage ?? row.NonBillablePercentage ?? row.nonBillable ?? row.NonBillable ?? 0,
  allocatedByName: row.allocatedByName ?? row.AllocatedByName ?? row.allocatedBy ?? row.AllocatedBy ?? '',
  allocatedAt: row.allocatedAt ?? row.AllocatedAt ?? row.allocationDate ?? row.AllocationDate,
});

const formatPercentage = (value) => `${Number(value || 0)}%`;

const ResourcesByProject = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleting, setDeleting] = useState(false);

  const loggedInUser = AuthContext.getCachedUser();
  const loggedInUserEmail = (loggedInUser?.userName || loggedInUser?.profile?.unique_name || '').toLowerCase();

  useEffect(() => {
    const fetchAssignedProjects = async () => {
      setLoadingProjects(true);
      try {
        const response = await axios.get(
          `${GET_ASSIGNED_PROJECTS_URL}&allocatorEmail=${encodeURIComponent(loggedInUserEmail)}`
        );
        const uniqueProjects = uniqueBy(
          normalizeResponse(response.data),
          (project) => project.projectId ?? project.id
        );

        setProjects(uniqueProjects.map(mapProjectOption));
      } catch (error) {
        message.error('Unable to load assigned projects. Please refresh the page.');
      } finally {
        setLoadingProjects(false);
      }
    };

    if (loggedInUserEmail) {
      fetchAssignedProjects();
    }
  }, [loggedInUserEmail]);

  const loadProjectResources = async () => {
    if (!selectedProjectId) {
      message.warning('Please select a project first.');
      return;
    }

    setLoadingUsers(true);
    setTableData([]);
    setSelectedRows([]);

    try {
      const response = await axios.get(
        `${GET_PROJECT_USERS_URL}&projectId=${encodeURIComponent(selectedProjectId)}`
      );

      const rows = normalizeResponse(response.data).map(mapUserRow);
      setTableData(rows);

      if (!rows.length) {
        message.info('No Allocations were found for this project.');
      }
    } catch (error) {
      const responseData = error?.response?.data;
      const apiMessage =
        responseData?.Message ||
        responseData?.message ||
        (typeof responseData === 'string' ? responseData : null);
      message.error(apiMessage || 'Unable to load project resources.');
    } finally {
      setLoadingUsers(false);
    }
  };

  const deleteAllocations = async (allocIds) => {
    if (!allocIds?.length) {
      return;
    }

    setDeleting(true);

    try {
      await Promise.all(
        allocIds.map((allocId) =>
          axios.delete(`${DELETE_ALLOCATION_URL}${encodeURIComponent(allocId)}?code=${masterHostKeyCode}${subscribeKey}`)
        )
      );

      message.success(
        allocIds.length === 1
          ? 'Allocation deleted successfully.'
          : `${allocIds.length} allocations deleted successfully.`
      );
      setSelectedRows([]);
      await loadProjectResources();
    } catch (error) {
      const responseData = error?.response?.data;
      const apiMessage =
        responseData?.Message ||
        responseData?.message ||
        (typeof responseData === 'string' ? responseData : null);
      message.error(apiMessage || 'Unable to delete allocation(s).');
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = (allocIds) => {
    const count = allocIds.length;

    Modal.confirm({
      title: count === 1 ? 'Delete Allocation' : `Delete ${count} Allocations`,
      content:
        count === 1
          ? 'Are you sure you want to delete this allocation?'
          : `Are you sure you want to delete ${count} selected allocations?`,
      okText: 'Delete',
      okButtonProps: { danger: true, loading: deleting },
      onOk: () => deleteAllocations(allocIds),
    });
  };

  const rowSelection = {
    selectedRowKeys: selectedRows.map((row) => row.key),
    onChange: (_, selected) => setSelectedRows(selected),
  };

  const columns = [
    {
      title: 'User Name',
      dataIndex: 'userName',
      key: 'userName',
      ellipsis: true,
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (value) => formatSriLankaDate(value),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (value) => formatSriLankaDate(value),
    },
    {
      title: 'Billable %',
      dataIndex: 'billablePercentage',
      key: 'billablePercentage',
      align: 'center',
      render: formatPercentage,
    },
    {
      title: 'Non Billable %',
      dataIndex: 'nonBillablePercentage',
      key: 'nonBillablePercentage',
      align: 'center',
      render: formatPercentage,
    },
    {
      title: 'Allocated By',
      dataIndex: 'allocatedByName',
      key: 'allocatedByName',
      ellipsis: true,
    },
    {
      title: 'Allocated At (SL)',
      dataIndex: 'allocatedAt',
      key: 'allocatedAt',
      render: (value) => formatSriLankaDateTime(value),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, row) => (
        <>
          <Button
            type="link"
            danger
            className="resource-allocation-delete-button"
            onClick={() => confirmDelete([row.allocId])}
          >
            Delete
          </Button>
        </>
      ),
    },
  ];

  return (
    <div className="table-container resource-allocation-page">
      <div className="resource-allocation-shell">
        <div className="resource-allocation-toolbar">
          <div>
            <h2>Allocations by Project</h2>
            <p>Select a project to view its allocated resources. Email addresses are not shown.</p>
          </div>
          <div className="resource-allocation-search">
            <Select
              showSearch
              allowClear
              placeholder="Select or type a project"
              value={selectedProjectId}
              onChange={(value) => {
                setSelectedProjectId(value);
                setTableData([]);
                setSelectedRows([]);
              }}
              options={projects}
              optionFilterProp="label"
              className="resource-allocation-person-select"
              loading={loadingProjects}
            />
            <Button
              type="primary"
              onClick={loadProjectResources}
              className="resource-allocation-button"
              loading={loadingUsers}
              disabled={!selectedProjectId}
            >
              View Resources
            </Button>
          </div>
        </div>

        <div className="resource-allocation-table-wrap">
          <div className="resource-allocation-table-toolbar">
            <Button
              danger
              onClick={() => confirmDelete(selectedRows.map((row) => row.allocId))}
              disabled={!selectedRows.length}
              loading={deleting}
              className="resource-allocation-button"
            >
              Delete Selected{selectedRows.length ? ` (${selectedRows.length})` : ''}
            </Button>
          </div>
          <Table
            rowKey="key"
            columns={columns}
            dataSource={tableData}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            tableLayout="fixed"
            size="middle"
            loading={loadingUsers}
            className="resource-allocation-table"
            rowSelection={rowSelection}
            locale={{
              emptyText: !selectedProjectId
                ? 'Select a project and click View Resources.'
                : 'No Allocations found for the selected project.',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ResourcesByProject;
